import { EMPIRE_PERSONAL_PHONE } from "./empire-contact";

// F117 (Pavel 2026-08-11): "potrebuju, aby chodily opravdu dobre napsane
// e-mailove notifikace. Aktualne je to treba casto jedna obecna veta. [...] Aby
// uz z e-mailu clovek vedel, o co jde. A aby to nebylo takovy suchy, jenom
// notifikacni e-mail, ale aby to byl dobre napsany notifikacni e-mail v kazde
// situaci."
//
// This is the list of every notification e-mail the product sends. It exists so
// the quality gate has something to iterate over: a new notification e-mail is
// only finished once it is listed here, in both locales, with a body that
// carries what happened, what it means and one thing to do.
//
// The rules the gate enforces live in
// launchpad/docs/decisions/F117-every-notification-e-mail-written-not.md.

export type MailAudience = "client" | "staff";

export interface NotificationMail {
  /** Stable id for the gate, the preview harness and the report. */
  readonly id: string;
  /** notifications.type this copy renders under. */
  readonly type: string;
  readonly audience: MailAudience;
  /** One line: when it fires. Read by humans, not by code. */
  readonly when: string;
  /** Null only when a human types the text at send time. */
  readonly subjectKey: string | null;
  readonly bodyKey: string | null;
  /** Every mail ends in one way out. Null is only for human-authored bodies. */
  readonly ctaKey: string | null;
  /** Sample values the gate and the preview render the copy with. */
  readonly variables?: Readonly<Record<string, string>>;
  /**
   * Written reason a body may sit under the two-sentence floor. The only
   * bodies that earn it are the ones whose entire content IS the button.
   */
  readonly terseReason?: string;
  /**
   * The body is typed by a human at send time (partner to client), so its
   * wording is theirs. Only the chrome around it is gated.
   */
  readonly authoredByHuman?: boolean;
  /**
   * A staff mail that is about no client at all (the delivery probe), so the
   * F94 rule "every staff subject opens with {client}" cannot apply to it.
   */
  readonly clientless?: boolean;
}

export const NOTIFICATION_MAILS: readonly NotificationMail[] = [
  // ---- client: getting in ----
  {
    id: "magic_link",
    type: "welcome_magic_link",
    audience: "client",
    when: "A sign-in link, on client creation and on every later request.",
    subjectKey: "mail.loginSubject",
    bodyKey: "mail.loginIntro",
    ctaKey: "mail.loginCta",
    terseReason: "The link IS the message; anything more delays the one click."
  },
  // F153: the SAME sign-in link, after a PURCHASE. It is the buyer's first
  // message ever, it arrives unbidden, and "Launchpad" is a word they have not
  // heard yet - so the terse reason above ("the link IS the message") stops
  // holding and one sentence of context is what makes an anonymous link read as
  // a receipt. The mechanism is untouched: same type, same subject, same
  // button, same seven-day credential. Only the body differs, which is why
  // reverting this is one locale key and one branch.
  {
    id: "magic_link_purchase_start",
    type: "welcome_magic_link",
    audience: "client",
    when: "A Start purchase created the client (Stripe metadata productType=start).",
    subjectKey: "mail.loginSubject",
    bodyKey: "mail.loginIntroStart",
    ctaKey: "mail.loginCta"
  },
  {
    id: "magic_link_purchase_empire",
    type: "welcome_magic_link",
    audience: "client",
    when: "An Empire purchase created the client (Stripe metadata productType=empire).",
    subjectKey: "mail.loginSubject",
    bodyKey: "mail.loginIntroEmpire",
    ctaKey: "mail.loginCta",
    // The digits live in ONE place (shared/empire-contact.ts) and this READS
    // that place rather than copying it: a sample the gate judges the copy by
    // and a number the product really sends must not be able to differ.
    variables: { phone: EMPIRE_PERSONAL_PHONE }
  },
  {
    id: "return_magic_link",
    type: "return_magic_link",
    audience: "client",
    when: "The same sign-in link, re-issued from the sign-in page or by an admin.",
    subjectKey: "mail.loginSubject",
    bodyKey: "mail.loginIntro",
    ctaKey: "mail.loginCta",
    terseReason: "The link IS the message; anything more delays the one click."
  },
  {
    id: "login_no_account",
    type: "login_no_account",
    audience: "client",
    when: "Someone asked for a sign-in link for an address with no account (F177); at most once an hour per address.",
    subjectKey: "mail.noAccountSubject",
    bodyKey: "mail.noAccountIntro",
    ctaKey: "mail.noAccountCta"
  },
  {
    id: "team_invite",
    type: "team_invite",
    audience: "client",
    when: "The owner invites a colleague into their Launchpad.",
    subjectKey: "mail.inviteSubject",
    bodyKey: "mail.inviteIntro",
    ctaKey: "mail.inviteCta",
    variables: { name: "Markéta Procházková" }
  },
  {
    id: "team_invite_nameless",
    type: "team_invite",
    audience: "client",
    when: "Same invite queued before F94, with no client name in the payload.",
    subjectKey: "mail.inviteSubject",
    bodyKey: "mail.inviteIntroNoName",
    ctaKey: "mail.inviteCta"
  },

  // ---- client: the journey moves ----
  {
    id: "section_unlocked",
    type: "section_unlocked",
    audience: "client",
    when: "A step opened and the payload names several sections or none.",
    subjectKey: "mail.sectionSubject",
    bodyKey: "mail.sectionIntro",
    ctaKey: "mail.sectionCta"
  },
  {
    id: "section_unlocked_named",
    type: "section_unlocked",
    audience: "client",
    when: "A step opened and the payload names exactly one known section.",
    subjectKey: "mail.sectionSubjectNamed",
    bodyKey: "mail.sectionIntroNamed",
    ctaKey: "mail.sectionCta",
    variables: { step: "Know-how" }
  },
  {
    id: "app_prefill_ready",
    type: "app_prefill_ready",
    audience: "client",
    when: "F136c: the machine finished prefilling the app questionnaire from the client's own answers, strategy and sales page. The step's OPENING is not announced: it opens on the client's own sales page approval, so they are standing there when it happens.",
    subjectKey: "mail.appPrefillReadySubject",
    bodyKey: "mail.appPrefillReadyIntro",
    ctaKey: "mail.appPrefillReadyCta"
  },
  {
    id: "call_confirmed",
    type: "section_unlocked",
    audience: "client",
    when: "The client signed up for a call occurrence (S01).",
    subjectKey: "mail.callSubject",
    bodyKey: "mail.callIntro",
    ctaKey: "mail.callCta"
  },
  {
    id: "call_reminder_day",
    type: "call_reminder_day",
    audience: "client",
    when: "Morning of the call.",
    subjectKey: "mail.callReminder.day.subject",
    bodyKey: "mail.callReminder.day.intro",
    ctaKey: "mail.callReminder.day.cta",
    variables: { time: "14:00" }
  },
  {
    id: "call_reminder_h3",
    // F131: the RUNG is `h3`, the notifications.type it queues under is
    // `call_reminder_3h` (`CALL_REMINDER_TYPES` in notify-triggers.ts). The
    // catalog named the rung, so these two entries described a type the product
    // never sends - invisible to the F117 gate, which only scans literal types
    // and this one is built from a lookup.
    type: "call_reminder_3h",
    audience: "client",
    when: "Three hours before the call.",
    subjectKey: "mail.callReminder.h3.subject",
    bodyKey: "mail.callReminder.h3.intro",
    ctaKey: "mail.callReminder.h3.cta",
    variables: { time: "14:00" }
  },
  {
    id: "call_reminder_m15",
    type: "call_reminder_15m",
    audience: "client",
    when: "Fifteen minutes before the call.",
    subjectKey: "mail.callReminder.m15.subject",
    bodyKey: "mail.callReminder.m15.intro",
    ctaKey: "mail.callReminder.m15.cta",
    variables: { time: "14:00" }
  },
  {
    id: "call_reminder_live",
    type: "call_reminder_live",
    audience: "client",
    when: "The call is running and the client has not joined.",
    subjectKey: "mail.callReminder.live.subject",
    bodyKey: "mail.callReminder.live.intro",
    ctaKey: "mail.callReminder.live.cta"
  },
  {
    id: "call_no_show",
    type: "call_no_show",
    audience: "client",
    when: "The call is over and the enrolled client never attended.",
    subjectKey: "mail.noShowSubject",
    bodyKey: "mail.noShowIntro",
    ctaKey: "mail.noShowCta"
  },
  {
    id: "questionnaire_nudge",
    type: "questionnaire_nudge",
    audience: "client",
    when: "A questionnaire step has been sitting open for days.",
    subjectKey: "mail.qNudgeSubject",
    bodyKey: "mail.qNudgeIntro",
    ctaKey: "mail.qNudgeCta",
    variables: { step: "Dotazník k přípravě tvého Buddyho" }
  },
  {
    id: "knowhow_shared",
    type: "knowhow_shared",
    audience: "client",
    when: "The Drive folder was provisioned and shared with the client account.",
    subjectKey: "mail.knowhowSharedSubject",
    bodyKey: "mail.knowhowSharedIntro",
    ctaKey: "mail.knowhowSharedCta",
    variables: { email: "marketa@example.cz" }
  },

  // ---- client: materials ----
  {
    id: "deliverable_review",
    type: "deliverable_review",
    audience: "client",
    when: "The partner published a version of a material to the client.",
    subjectKey: "mail.deliverableReviewSubject",
    bodyKey: "mail.deliverableReviewIntro",
    ctaKey: "mail.deliverableOpenCta",
    variables: { material: "Prodejní stránka", version: "3" }
  },
  {
    id: "deliverable_feedback_resolved",
    type: "deliverable_feedback_resolved",
    audience: "client",
    when: "The published version answers every open client comment.",
    subjectKey: "mail.deliverableResolvedSubject",
    bodyKey: "mail.deliverableResolvedIntro",
    ctaKey: "mail.deliverableOpenCta",
    variables: { material: "E-maily" }
  },
  {
    id: "order_links_integrated",
    type: "order_links_integrated",
    audience: "client",
    when: "Alex merged the order links into the approved sales page.",
    subjectKey: "mail.orderLinksSubject",
    bodyKey: "mail.orderLinksIntro",
    ctaKey: "mail.orderLinksCta"
  },
  {
    id: "staff_override_reopened",
    type: "staff_override",
    audience: "client",
    when: "The partner returned a client-approved step for another pass.",
    subjectKey: "mail.staffReopenedSubject",
    bodyKey: "mail.staffReopenedBody",
    ctaKey: "mail.openLaunchpad",
    variables: { material: "Prodejní stránka", reason: "ceník ještě neodpovídá tomu, na čem jsme se domluvili" }
  },
  {
    id: "staff_override_approved",
    type: "staff_override",
    audience: "client",
    when: "The partner approved a step on the client's behalf after a call.",
    subjectKey: "mail.staffApprovedSubject",
    bodyKey: "mail.staffApprovedBody",
    ctaKey: "mail.openLaunchpad",
    variables: { material: "Návrh prodejní strategie", reason: "odsouhlaseno na hovoru 11.8." }
  },
  {
    id: "partner_assigned",
    type: "partner_assigned",
    audience: "client",
    when: "F139: an admin assigned the client their FIRST partner. Moving a client from one partner to another stays silent - they already have a named partner either way.",
    subjectKey: "mail.partnerAssignedSubject",
    bodyKey: "mail.partnerAssignedIntro",
    ctaKey: "mail.openLaunchpad",
    variables: { partner: "Tomáš Pohl" }
  },

  // ---- client: the bot changes hands ----
  {
    id: "bot_transfer_ready",
    type: "bot_transfer_ready",
    audience: "client",
    when: "The manual transfer path put the ball on the client's side (S18).",
    subjectKey: "mail.botTransferReadySubject",
    bodyKey: "mail.botTransferReadyIntro",
    ctaKey: "mail.botTransferReadyCta"
  },
  {
    id: "bot_transfer_done",
    type: "bot_transfer_done",
    audience: "client",
    when: "The bot verifiably left our pool account and is the client's.",
    subjectKey: "mail.botTransferDoneSubject",
    bodyKey: "mail.botTransferDoneIntro",
    ctaKey: "mail.botTransferDoneCta"
  },
  {
    id: "bot_transfer_client_fix",
    type: "bot_transfer_client_fix",
    audience: "client",
    when: "The transfer failed for a reason only the client can clear (F112).",
    subjectKey: "mail.botTransferFixSubject",
    bodyKey: "mail.botTransferFixIntro",
    ctaKey: "mail.botTransferFixCta",
    variables: { detail: "zadané jméno nepatří osobnímu účtu, ale botovi nebo kanálu" }
  },

  // ---- client: written by a human ----
  {
    id: "partner_message",
    type: "partner_message",
    audience: "client",
    when: "The partner writes their client a message from the console.",
    // Subject and body are the partner's own words; only the button is ours.
    subjectKey: null,
    bodyKey: null,
    ctaKey: "mail.openLaunchpad",
    authoredByHuman: true
  },
  {
    id: "system_test",
    type: "system_test",
    audience: "client",
    when: "An admin proves the notification layer reaches an address.",
    subjectKey: "notifications.testSubject",
    bodyKey: "notifications.testIntro",
    ctaKey: "notifications.testCta"
  },
  {
    id: "admin_delivery_test",
    type: "admin_delivery_test",
    audience: "staff",
    when: "The Servis notification panel tests delivery against the acting admin's own address.",
    subjectKey: "notifications.testSubject",
    bodyKey: "notifications.testIntro",
    ctaKey: "notifications.testCta",
    // The one staff mail that is deliberately not about a client: it is a probe
    // of the channel itself, so the F94 {client} prefix would name nobody.
    clientless: true
  },

  // ---- staff: the team is on turn ----
  {
    id: "action_required",
    type: "action_required",
    audience: "staff",
    when: "A transition put a step on the team's side.",
    subjectKey: "mail.actionSubject",
    bodyKey: "mail.actionIntro",
    ctaKey: "mail.openClientConsole"
  },
  {
    id: "transcript_failed",
    type: "transcript_failed",
    audience: "staff",
    when: "Automatic transcription of a client recording gave up.",
    subjectKey: "mail.transcriptSubject",
    bodyKey: "mail.transcriptIntro",
    ctaKey: "mail.openClientConsole"
  },
  {
    id: "training_started",
    type: "training_started",
    audience: "staff",
    when: "The Instance Creator picked the client up and the training run began.",
    subjectKey: "mail.trainingStartedTitle",
    bodyKey: "mail.trainingStartedBody",
    ctaKey: "mail.openClientConsole"
  },
  {
    id: "training_failed",
    type: "training_failed",
    audience: "staff",
    when: "The training run stopped with an error and needs a human.",
    subjectKey: "mail.trainingFailedTitle",
    bodyKey: "mail.trainingFailedBody",
    ctaKey: "mail.openClientConsole"
  },
  {
    id: "dfy_q2_ready",
    type: "dfy_q2_ready",
    audience: "staff",
    when: "The client submitted the sales questionnaire and the build started.",
    subjectKey: "mail.dfyQ2ReadyTitle",
    bodyKey: "mail.dfyQ2ReadyBody",
    ctaKey: "mail.dfyOpenClient"
  },
  {
    id: "handover_owner_role",
    type: "handover_owner_role",
    audience: "client",
    when: "The instance owner role landed; step 3 of the handover can finish.",
    subjectKey: "mail.handoverOwnerRoleSubject",
    bodyKey: "mail.handoverOwnerRoleBody",
    ctaKey: "mail.openLaunchpad"
  },
  {
    id: "owner_role_client_fix",
    type: "owner_role_client_fix",
    audience: "client",
    when: "The automatic owner-role set refused the client's Telegram ID; the form is open again.",
    subjectKey: "mail.ownerRoleFixSubject",
    bodyKey: "mail.ownerRoleFixIntro",
    ctaKey: "mail.ownerRoleFixCta",
    variables: { detail: "ID není platné" }
  },
  {
    id: "owner_role_staff_failed",
    type: "owner_role_staff_failed",
    audience: "staff",
    when: "The automatic owner-role set failed internally; a human decides the next move.",
    subjectKey: "mail.ownerRoleFailedTitle",
    bodyKey: "mail.ownerRoleFailedIntro",
    ctaKey: "mail.openClientConsole",
    variables: { reason: "session_unavailable" }
  },
  {
    id: "deliverable_edit_failed",
    type: "deliverable_edit_failed",
    audience: "staff",
    when: "An Alex edit of a material (incl. order links) did not go through; the material is unchanged.",
    subjectKey: "mail.editFailedTitle",
    bodyKey: "mail.editFailedBody",
    ctaKey: "mail.dfyOpenClient",
    variables: { material: "Prodejní stránka" }
  },
  {
    id: "deliverable_partner_review",
    type: "deliverable_partner_review",
    audience: "staff",
    when: "A build finished a material the partner has to review and publish.",
    subjectKey: "mail.dfyDeliverableReadyTitle",
    bodyKey: "mail.dfyDeliverableReadyBody",
    ctaKey: "mail.dfyOpenClient",
    variables: { deliverableTitle: "Prodejní stránka" }
  },
  {
    id: "deliverable_partner_revised",
    type: "deliverable_partner_review",
    audience: "staff",
    when: "Alex rebuilt a material after the client's comments.",
    subjectKey: "mail.dfyDeliverableRevisedTitle",
    bodyKey: "mail.dfyDeliverableRevisedBody",
    ctaKey: "mail.dfyOpenClient",
    variables: { deliverableTitle: "E-maily", version: "4" }
  },
  // F157: the ONE message a sales_only import sends about itself. It exists so
  // the import's own steps can stay silent: the same run used to queue six
  // mails inside a minute (four materials, the questionnaire, the transition),
  // four of them with an identical subject.
  {
    id: "sales_only_import_summary",
    type: "sales_only_import_summary",
    audience: "staff",
    when: "A sales_only import finished; this is the whole of what it brought, in one mail.",
    subjectKey: "mail.salesImportSummaryTitle",
    bodyKey: "mail.salesImportSummaryBody",
    ctaKey: "mail.openClientConsole",
    variables: { summary: "Hotovo: 4 materiály ke kontrole, odeslaný prodejní dotazník, schválená strategie." }
  },
  {
    id: "deliverable_client_note",
    type: "deliverable_client_note",
    audience: "staff",
    when: "The client left comments on a material (folded per 30 minutes).",
    subjectKey: "mail.clientNoteTitle",
    bodyKey: "mail.clientNoteBody",
    ctaKey: "mail.dfyOpenClient",
    variables: { material: "Prodejní stránka" }
  },
  {
    id: "client_partner_message",
    type: "client_partner_message",
    audience: "staff",
    when: "The client wrote to their partner from the partner card (F162: the mailto button did nothing on a machine with no mail app).",
    subjectKey: "mail.clientMessageTitle",
    bodyKey: "mail.clientMessageBody",
    ctaKey: "mail.openClientConsole",
    variables: { email: "klient@example.cz" }
  },
  {
    id: "deliverable_build_failed",
    type: "deliverable_build_failed",
    audience: "staff",
    when: "An automated build ended in an error state.",
    subjectKey: "mail.buildFailedTitle",
    bodyKey: "mail.buildFailedBody",
    ctaKey: "mail.dfyOpenClient",
    variables: { material: "E-maily" }
  },
  {
    id: "gate_approved",
    type: "gate_approved",
    audience: "staff",
    when: "The client approved a material and the next build step started.",
    subjectKey: "mail.gateApprovedTitle",
    bodyKey: "mail.gateApprovedBody",
    ctaKey: "mail.openClientConsole",
    variables: { material: "Prodejní stránka", version: "3" }
  },
  {
    id: "training_result",
    type: "training_result",
    audience: "staff",
    when: "Instance Creator finished training and the instance waits for approval.",
    subjectKey: "mail.trainingReadyTitle",
    bodyKey: "mail.trainingReadyBody",
    ctaKey: "mail.openClientConsole",
    variables: { botSuffix: " (bot @marketa_buddy_bot)" }
  },
  {
    id: "app_wizard_submitted",
    type: "app_wizard_submitted",
    audience: "staff",
    when: "The client submitted the mobile app materials.",
    subjectKey: "mail.appWizardSubmittedTitle",
    bodyKey: "mail.appWizardSubmittedBody",
    ctaKey: "mail.openClientConsole"
  },
  {
    id: "instance_team_done",
    type: "instance_team",
    audience: "staff",
    when: "The partner was added to the instance team.",
    subjectKey: "mail.instanceTeamDoneTitle",
    bodyKey: "mail.instanceTeamDoneBody",
    ctaKey: "mail.openClientConsole",
    variables: { bot: "marketa_buddy_bot" }
  },
  {
    id: "instance_team_failed",
    type: "instance_team",
    audience: "staff",
    when: "Adding the partner to the instance team failed.",
    subjectKey: "mail.instanceTeamFailedTitle",
    bodyKey: "mail.instanceTeamFailedBody",
    ctaKey: "mail.openClientConsole",
    variables: { bot: "marketa_buddy_bot", noteSuffix: " (uživatel botovi nikdy nenapsal)" }
  },
  {
    id: "partner_assigned_staff",
    type: "partner_assigned_staff",
    audience: "staff",
    when: "F139: the admin handed this client to the partner. Sent on the FIRST assignment only, to the partner who got them.",
    subjectKey: "mail.partnerAssignedStaffTitle",
    bodyKey: "mail.partnerAssignedStaffBody",
    ctaKey: "mail.openClientConsole",
    variables: { admin: "Tomáš Pohl" }
  },
  {
    id: "bot_transfer_staff_failed",
    type: "bot_transfer_staff_failed",
    audience: "staff",
    when: "The automatic bot transfer failed for a reason the team owns.",
    subjectKey: "mail.botTransferFailedTitle",
    bodyKey: "mail.botTransferFailedIntro",
    ctaKey: "mail.openClientConsole",
    variables: { reason: "dvoufázové ověření účtu je mladší než 7 dní" }
  }
];

/** The gate needs one entry per id; a duplicate would silently shadow a mail. */
export const NOTIFICATION_MAIL_IDS: readonly string[] = NOTIFICATION_MAILS.map((mail) => mail.id);

/**
 * Notifications that reach the client as a PUSH and never as mail, each with
 * the reason it carries no e-mail copy. The F117 gate reads this to tell a
 * deliberate push-only type from a mail somebody forgot to write.
 *
 * 🔴 F138 kolečko 2 moved it here from inside `tests/notifikacni-maily-f117.test.ts`.
 * The queue panes render a human NAME per notification type, and that name has
 * to exist for every type the product can queue - including these. A list that
 * lives only in a test cannot be read at runtime, so the console would have
 * grown a second, hand-typed inventory of types beside this one. That is
 * exactly how the old `NOTIFICATION_TYPE_NAMES` drifted until seven of its
 * eight entries named no real notification at all.
 */
export const PUSH_ONLY_NOTIFICATIONS: readonly { readonly type: string; readonly reason: string }[] = [
  {
    type: "profile_map_applied",
    reason: "push only - the client is looking at the profile the map just changed"
  }
];

/**
 * Every notification type the product can queue: the mails above plus the
 * push-only ones. This is the list the queue panes name their rows from, and
 * the one `tests/aktivita-f138.test.ts` walks to make sure each has a human
 * name in BOTH locales.
 */
export const NOTIFICATION_TYPES: readonly string[] = [
  ...new Set([
    ...NOTIFICATION_MAILS.map((mail) => mail.type),
    ...PUSH_ONLY_NOTIFICATIONS.map((entry) => entry.type)
  ])
].sort();
